// ═══════════════════════════════════════════════════════════════════
// TMS GAMMA — Painel de Alertas Internos (flutuante)
// ═══════════════════════════════════════════════════════════════════
import React, { useState } from "react";
import { T, syne, mono } from "../../theme/tokens";
import { fmtQuando } from "../../utils/formatters";
import { Icone, ICONES } from "./Icone";

const COR = { critico: "#ef4444", atencao: "#f59e0b", info: "#3b82f6" };

export function PainelAlertas({ alertas, onNav }) {
  const [aberto, setAberto] = useState(false);
  const [lidos,  setLidos]  = useState(new Set());

  const naoLidos   = alertas.filter(a => !lidos.has(a.id));
  const criticos   = alertas.filter(a => a.tipo === "critico").length;

  // Duas listas de propósito (#141): o administrador passa a receber as
  // divergências que a equipe apurou, e com tudo junto os alertas dele
  // sumiriam no meio num dia de conferência.
  const meus   = alertas.filter(a => !a.deOutro);
  const outros = alertas.filter(a =>  a.deOutro);

  function marcarLido(id) { setLidos(p => new Set([...p, id])); }
  function marcarTodos()  { setLidos(new Set(alertas.map(a => a.id))); }

  return (
    <>
      {/* ── Sino — inline no header (não fixed) ─────────────────── */}
      <button type="button" onClick={() => setAberto(a => !a)}
        title="Alertas do sistema" aria-label="Alertas do sistema"
        style={{
          position:"relative",
          width:34, height:34, borderRadius:"50%",
          background: criticos > 0 ? "#ef444420" : T.surface,
          border: `1px solid ${criticos > 0 ? "#ef4444" : T.border}`,
          cursor:"pointer", display:"flex", alignItems:"center",
          justifyContent:"center", transition:"var(--m-cor)", flexShrink:0,
        }}>
        <svg width={18} height={18} viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"
          stroke={criticos > 0 ? "#ef4444" : T.muted}
          strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        {naoLidos.length > 0 && (
          <div style={{
            position:"absolute", top:-3, right:-3,
            width:16, height:16, borderRadius:"50%",
            background: criticos > 0 ? "#ef4444" : "#f59e0b",
            fontSize:8, fontWeight:800, color:"#fff",
            display:"flex", alignItems:"center", justifyContent:"center",
          }}>
            {naoLidos.length > 9 ? "9+" : naoLidos.length}
          </div>
        )}
      </button>

      {/* ── Painel de alertas ───────────────────────────────────── */}
      {aberto && (
        <div style={{
          position:"fixed", top:56, right:12, zIndex:650,
          width:380, maxHeight:520,
          background:T.card, border:`1px solid ${T.border2||T.border}`,
          borderRadius:12, boxShadow:"0 16px 48px rgba(0,0,0,.5)",
          display:"flex", flexDirection:"column", overflow:"hidden",
        }}>
          {/* Header */}
          <div style={{ padding:"12px 16px", borderBottom:`1px solid ${T.border}`,
            display:"flex", alignItems:"center", justifyContent:"space-between",
            background:T.surface, flexShrink:0 }}>
            <div style={{ fontSize:13, fontWeight:700, display:"flex", alignItems:"center", gap:6, ...syne }}>
              <Icone d={ICONES.alerta} size={15}/> Alertas do Sistema
            </div>
            <div style={{ display:"flex", gap:8, alignItems:"center" }}>
              {naoLidos.length > 0 && (
                <button type="button" onClick={marcarTodos}
                  style={{ fontSize:9, color:T.muted, background:"none",
                    border:`1px solid ${T.border}`, borderRadius:5,
                    padding:"3px 8px", cursor:"pointer", ...syne }}>
                  Marcar todos como lidos
                </button>
              )}
              <button type="button" onClick={() => setAberto(false)} aria-label="Fechar alertas"
                style={{ background:"none", border:"none", color:T.muted,
                  cursor:"pointer", fontSize:16, lineHeight:1 }}>×</button>
            </div>
          </div>

          {/* Lista de alertas */}
          <div style={{ overflowY:"auto", flex:1, padding:"8px" }}>
            {alertas.length === 0 && (
              <div style={{ padding:"32px 16px", textAlign:"center" }}>
                <div style={{ display:"flex", justifyContent:"center", marginBottom:8, color:T.green }}><Icone d={ICONES.confirmar} size={24}/></div>
                <div style={{ fontSize:12, color:T.muted }}>
                  Nenhum alerta ativo. Sistema operando normalmente.
                </div>
              </div>
            )}
            {meus.map(cartao)}

            {outros.length > 0 && (
              <>
                <div style={{ fontSize:9, fontWeight:800, color:T.muted, ...syne,
                  textTransform:"uppercase", letterSpacing:".5px",
                  margin:"12px 4px 6px", display:"flex", alignItems:"center", gap:6 }}>
                  <span style={{ flex:1, height:1, background:T.border }}/>
                  De outros usuários ({outros.length})
                  <span style={{ flex:1, height:1, background:T.border }}/>
                </div>
                {outros.map(cartao)}
              </>
            )}
          </div>

          {/* Footer */}
          {alertas.length > 0 && (
            <div style={{ padding:"8px 14px", borderTop:`1px solid ${T.border}`,
              fontSize:9, color:T.muted, textAlign:"center", flexShrink:0 }}>
              {criticos > 0 && <span style={{ color:"#ef4444", fontWeight:700 }}>
                {criticos} crítico(s) · </span>}
              {alertas.length} alerta(s) total · atualizado em tempo real
            </div>
          )}
        </div>
      )}
    </>
  );

  // Um cartão de alerta. Virou função para as duas seções — a minha e a de
  // outros usuários — desenharem o mesmo cartão sem duplicar 40 linhas.
  function cartao(a) {
    const cor  = COR[a.tipo] || T.muted;
    const lido = lidos.has(a.id);
    return (
        <div key={a.id}
          style={{
            background: lido ? "transparent" : cor+"0C",
            border:`1px solid ${lido ? T.border : cor+"33"}`,
            borderLeft:`3px solid ${lido ? T.border : cor}`,
            borderRadius:8, padding:"10px 12px", marginBottom:6,
            opacity: lido ? 0.55 : 1, transition:"var(--m-cor)",
          }}>
          <div style={{ display:"flex", justifyContent:"space-between",
            alignItems:"flex-start", gap:8 }}>
            <div style={{ flex:1 }}>
              <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:3 }}>
                <span style={{ display:"inline-flex", color:cor }}><Icone d={ICONES[a.icone] || ICONES.alerta} size={14}/></span>
                <span style={{ fontSize:11, fontWeight:700, color:cor, ...syne }}>
                  {a.titulo}
                </span>
                {a.valor != null && (
                  <span style={{ fontSize:10, ...mono, color:cor,
                    background:cor+"18", padding:"1px 6px", borderRadius:4 }}>
                    {a.valor}
                  </span>
                )}
              </div>
              <div style={{ fontSize:10, color:T.muted, lineHeight:1.5 }}>{a.msg}</div>
              {fmtQuando(a.em) && (
                <time dateTime={a.em} style={{ display:"block", fontSize:9, ...mono,
                  color:T.muted, marginTop:3, opacity:.8 }}>
                  {fmtQuando(a.em)}
                </time>
              )}
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:4, flexShrink:0 }}>
              {a.modulo && (
                <button type="button"
                  onClick={() => { onNav?.(a.modulo); setAberto(false); }}
                  style={{ fontSize:9, padding:"3px 7px", borderRadius:5,
                    border:`1px solid ${cor}55`, background:cor+"14",
                    color:cor, cursor:"pointer", ...syne, whiteSpace:"nowrap" }}>
                  Ver →
                </button>
              )}
              {!lido && (
                <button type="button" onClick={() => marcarLido(a.id)}
                  style={{ fontSize:9, padding:"3px 7px", borderRadius:5,
                    border:`1px solid ${T.border}`, background:"none",
                    color:T.muted, cursor:"pointer", ...syne }}>
                  ✓ Lido
                </button>
              )}
            </div>
          </div>
        </div>
    );
  }
}

